import { useEffect, useState, useMemo, useRef } from 'react'
import { useGetIdentity, useNotify, useRedirect } from 'react-admin'
import { ContainerLayout, Header } from '@react-admin/ra-navigation'
import { TourProvider } from '@react-admin/ra-tour'
import { Stack, useMediaQuery } from '@mui/material'
import { isEqual } from 'lodash'

import Tours from './Tours'
import CustomUserMenu from './UserMenu'
import { getRole } from './common/roles'
import { supabaseClient } from './supabase'
import LoadingPage from './Design/LoadingPage'
import { isUUID, safeUUID } from './common/isUUID'
import dataProvider from './supabase/dataProvider'
import CustomBreadCrumb from './Design/CustomBreadCrumb'
import CustomHorizontalMenu from './CustomHorizontalMenu'
import FrontUpdateAvailable from './Design/FrontUpdateAvailable'
import { useServiceWorker } from './contexts/ServiceWorkerContext'
import { getItemFromLocalStorage, setItemToLocalStorage } from './common/LocalStorage'

const CustomAppBar = ({ isMobile, isPubeur }) =>
  <Header title={(isMobile || ! isPubeur) ? '' : 'Forma Contacts'} />

const getLeadYears = async () => {
  const { data, error } = await supabaseClient
    .from('lead_years')
    .select('id, name, current')
    .order('created_at', { ascending: false })

  if ( error ) {
    console.error('Error getting lead years: ', error)
    return []
  }

  return data
}

export const MyLayout = ({ children, ...rest }) => {
  const { isUpdateAvailable } = useServiceWorker()
  
  const { data: identity } = useGetIdentity()
  const notify = useNotify()
  const redirect = useRedirect()
  const role = identity?.role?.grade ?? null
  const isPubeur = role < getRole('responsable')
  const isMobile = useMediaQuery(
    theme => theme.breakpoints.down('md'),
    { noSsr: true }
  )
  const [profileTours, setProfileTours] = useState(null)
  const [maintenanceStyle, setMaintenanceStyle] = useState({display: 'none'})

  // Replace useMemo with useRef for localStorage values
  const defaultCurrentYearLocalRef = useRef(getItemFromLocalStorage('localCurrentYear'))
  const cachedLeadYearsRef = useRef(getItemFromLocalStorage('leadYears', null, Array.isArray))
  const localCenterRef = useRef(getItemFromLocalStorage('localCenter'))
  const maintenanceStatusCheckRef = useRef(getItemFromLocalStorage('maintenanceStatusCheck'))
  const centerTagsRef = useRef(getItemFromLocalStorage('centerTags'))
  const profileToursStatusRef = useRef(getItemFromLocalStorage('profileToursStatus'))
  
  useEffect(() => {
    if (!identity) return

    const fetchData = async () => {
      if (!identity) return
    
      try {
        const promisesMap = {}

        if (!defaultCurrentYearLocalRef.current) {
          promisesMap.localCurrentYear = dataProvider.getOne('lead_years', { id: process.env.REACT_APP_CURRENT_YEAR_ID })
        }

        if (!cachedLeadYearsRef.current) {
          promisesMap.leadYears = getLeadYears()
        }

        if (!localCenterRef.current && identity?.center) {
          promisesMap.localCenter = Promise.resolve(identity?.center)
        }

        const resultsArray = await Promise.all(Object.values(promisesMap))

        // Map resolved results back to keys
        const results = Object.keys(promisesMap).reduce((acc, key, index) => {
          acc[key] = resultsArray[index]
          return acc
        }, {})

        // Store values in localStorage and update refs
        if (results.localCurrentYear?.data) {
          console.log('Setting localCurrentYear to localStorage')
          const data = results.localCurrentYear.data
          setItemToLocalStorage('localCurrentYear', data)
          defaultCurrentYearLocalRef.current = data
        }

        if (results.leadYears) {
          console.log('Setting leadYears to localStorage')
          const leadYears = results.leadYears
          setItemToLocalStorage('leadYears', leadYears)
          cachedLeadYearsRef.current = leadYears
        }

        if (results.localCenter) {
          console.log('Setting localCenter to localStorage')
          const center = results.localCenter
          setItemToLocalStorage('localCenter', center)
          localCenterRef.current = center
        }
      } catch (error) {
        console.error('Error setting up local data for the current user', error)
      }
    }

    fetchData()
  }, [identity])
  
  useEffect(() => {
    const getData = async () => {
      if ( ! isUUID(identity.center?.id) ) return

      const promises = []
  
      promises.push(
        supabaseClient
          .from('isUnderMaintenance')
          .select('status')
          .single()
      )
  
      const centerTags = centerTagsRef.current
      if (!centerTags || Date.now() - centerTags.updatedAt > 3600000) { // 1h
        promises.push(
          supabaseClient
            .from('tags')
            .select('*')
            .eq('center_id', safeUUID(identity.center.id))
        )
      } else {
        promises.push(Promise.resolve(null))
      }
      
      const [maintenanceResult, tagsResult] = await Promise.all(promises)
  
      if (maintenanceResult?.data) {
        setMaintenanceStyle({
          display: maintenanceResult.data.status ? 'flex' : 'none',
        })
        
        console.log('Setting maintenanceStatus to localStorage')

        const now = Date.now()
        setItemToLocalStorage('maintenanceStatusCheck', now)
        maintenanceStatusCheckRef.current = now
      }
  
      if (tagsResult?.data) {
        const tagsData = { tags: tagsResult.data, updatedAt: Date.now() }
        console.log('Setting centerTags to localStorage')
        setItemToLocalStorage('centerTags', tagsData)
        centerTagsRef.current = tagsData
      }
    }
    
    if (
      identity &&
      (!maintenanceStatusCheckRef.current || maintenanceStatusCheckRef.current < Date.now() - 3600000) // 1h
    ) {
      getData()
    }
  }, [identity, isPubeur])
  
  useEffect(() => {
    if (!identity || !profileTours) return
  
    const updateProfile = async () => {
      console.log('Init. Updating profile tours...')
  
      // Update localStorage for tracking and ref
      setItemToLocalStorage('profileToursStatus', profileTours)
      profileToursStatusRef.current = profileTours
  
      // Update tours status in the database
      const { error } = await supabaseClient
        .from('profiles')
        .update({ tours: profileTours })
        .eq('id', identity.id)
        .single()
  
      if (error) {
        console.error('Error updating profile: ', error)
      }
    }
  
    // Check if the tours have changed (e.g., new tours added)
    const profileToursStatus = profileToursStatusRef.current
    if (!profileToursStatus || !isEqual(profileToursStatus, profileTours)) {
      updateProfile()
    }
  }, [identity, profileTours])

  // Styles
  const memoizedStyle = useMemo(() => ({
    ...maintenanceStyle,
    backgroundColor: '#f79525',
    padding: '10px',
    fontSize: '.9rem',
    fontWeight: 600,
  }), [maintenanceStyle])

  return identity ? (
    <>
      {isUpdateAvailable && <FrontUpdateAvailable />}

      <TourProvider tours={Tours} tools={{ notify, redirect, profileTours, setProfileTours }}>
        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center" 
          justifyContent="center" 
          sx={memoizedStyle}
        >
          Une opération de maintenance est en cours. Merci de votre compréhension.
        </Stack>

        <Stack 
          direction="row" 
          spacing={2} 
          alignItems="center" 
          justifyContent="center" 
          sx={{
            display: process.env.REACT_APP_SUPABASE_URL?.includes('hrfrmccmvoqcsrizaemh') ? 'flex' : 'none',
            backgroundColor: '#f79525',
            padding: '10px',
            fontSize: '.9rem',
            fontWeight: 600,
          }}
        >
          Environnement de staging (tests CRM)
        </Stack>

        <ContainerLayout
          {...rest}
          appBar={<CustomAppBar isMobile={isMobile} isPubeur={isPubeur} />}
          maxWidth="xl"
          menu={<CustomHorizontalMenu role={role} identity={identity} />}
          userMenu={<CustomUserMenu />}
          sx={{
            backgroundColor: 'mint.main'
          }}
        >
          <CustomBreadCrumb />
          {children}
        </ContainerLayout>
      </TourProvider>
    </>
  ) : <LoadingPage />
}